import type { EnvironmentUtils } from '@change-corgi/core/environment';
import type { ErrorReporter } from '@change-corgi/core/errorReporter/common';
import type { HttpClient } from '@change-corgi/core/http';
import { createI18n, type I18n, type Translations } from '@change-corgi/core/i18n';

import { getTranslationsCacheBuster } from '#src/shared/i18n';
import type { Session } from '#src/shared/session';

import { setupI18n as _setupI18n } from '#src/app/i18n';
import { markTimelineEnd, markTimelineStart } from '#src/app/shared/performance';

export type Options = Readonly<{
	useServerTranslations: boolean;
	errorReporter: ErrorReporter;
	session: Session;
	http: HttpClient;
	environment: EnvironmentUtils;
	translationsIdentifier?: string;
	/**
	 * translations to use as a priority, generally coming from hydration data
	 */
	translations: Translations | undefined;
}>;

export async function setupI18n({
	errorReporter,
	session,
	http,
	environment,
	useServerTranslations,
	translationsIdentifier,
	translations,
}: Options): Promise<{ i18n: I18n; i18nError?: true }> {
	const {
		locale: { localeCode: locale },
	} = session;

	const env = environment.getDemoEnvironment() || environment.getEnvironment();
	const cacheBuster = getTranslationsCacheBuster({ env, translationsIdentifier });

	markTimelineStart('i18n');
	try {
		const i18n = await _setupI18n(
			{ http, errorReporter },
			{
				translations,
				locale,
				useServerTranslations,
				cacheBuster,
			},
		);
		markTimelineEnd('i18n');
		return { i18n };
	} catch (e) {
		const i18n = createI18n({
			locale,
			translations: {},
			reportError: errorReporter.report.bind(errorReporter),
		});
		markTimelineEnd('i18n');
		return { i18n, i18nError: true };
	}
}
